import * as React from "react";
import { useQueryClient } from "react-query";
import { useSocket, useSocketMessageHandler } from "contexts/socket";
import { GenericReviewStatus, ReviewResponse } from "../types";

type ModerationSocketMessage = {
  type: "MODERATION";
  moderation: {
    reviewId: string;
    status: GenericReviewStatus;
    counterUpvote: number;
    counterDownvote: number;
  };
};

/**
 * Subscribe to moderation messages from web socket
 */
const useReviewModeration = () => {
  const queryClient = useQueryClient();

  // Subscribe to moderation web socket messages
  const { ws } = useSocket();
  React.useEffect(() => {
    if (ws) {
      ws.send("moderation");
    }
  }, [ws]);

  // Moderation Messages - If the ID Matches the current ID then update the
  // counter in the query cache. We only show 1 review at a time so we can
  // restrict our check to the first item in the query cache.
  const onSocketMessage = React.useCallback(
    (message: ModerationSocketMessage) => {
      if (message.type === "MODERATION") {
        const queryData = queryClient.getQueryData<ReviewResponse>([
          "reviews",
          "pending",
        ]);
        const review = queryData?.data?.[0];
        if (review && review.reviewId === message.moderation.reviewId) {
          review.counterDownvote = message.moderation.counterDownvote;
          review.counterUpvote = message.moderation.counterUpvote;
          review.status = message.moderation.status;
          queryClient.setQueryData(["reviews", "pending"], queryData);
        }
      }
    },
    [queryClient],
  );

  useSocketMessageHandler<ModerationSocketMessage>(
    "MODERATION",
    onSocketMessage,
  );
};

export { useReviewModeration };
